"use client";

import type { AnalysisKey, AnalysisStatistics } from "@/lib/music/analyzer-types";

// ── Types ──────────────────────────────────────────────────────

interface Suggestion {
  label: string;
  prompt: string;
  contextual?: boolean;
}

interface PromptSuggestionsProps {
  onSelect: (prompt: string) => void;
  /** Current song key (for contextual suggestions) */
  songKey?: AnalysisKey | null;
  /** Current song statistics (for contextual suggestions) */
  songStats?: AnalysisStatistics | null;
  /** Current song title (for contextual suggestions) */
  songTitle?: string | null;
}

// ── Static suggestions ─────────────────────────────────────────

const STATIC_SUGGESTIONS: Suggestion[] = [
  { label: "Gamme Do majeur, 2 octaves", prompt: "Gamme de Do majeur sur 2 octaves, mains ensemble" },
  { label: "Arpeges majeurs et mineurs", prompt: "Arpeges majeurs et mineurs en Do, Fa et Sol sur 2 octaves" },
  { label: "Progression I-IV-V-I", prompt: "Progression d'accords I-IV-V-I en Do majeur, accords plaques, 4 mesures" },
  { label: "Rythme croise", prompt: "Exercice de rythme croise : croches a la main droite, noires a la main gauche" },
  { label: "Gamme blues en La", prompt: "Gamme blues en La mineur sur 2 octaves, mains alternees" },
  { label: "Hanon exercice n.1", prompt: "Exercice de type Hanon n.1 : motif montant et descendant sur 2 octaves" },
];

// ── Component ──────────────────────────────────────────────────

export function PromptSuggestions({
  onSelect,
  songKey,
  songStats,
  songTitle,
}: PromptSuggestionsProps) {
  // Build contextual suggestions based on loaded song
  const contextual: Suggestion[] = [];

  if (songTitle && songStats) {
    const topChords = songStats.chordFrequency
      .slice(0, 6)
      .map((c) => c.chord)
      .join(", ");

    if (topChords) {
      contextual.push({
        label: `Accords de ${songTitle}`,
        prompt: `Enchainement des accords de "${songTitle}" : ${topChords}. Accords plaques puis arpeges.`,
        contextual: true,
      });
    }
  }

  if (songKey) {
    contextual.push({
      label: `Gamme de ${songKey.label}`,
      prompt: `Gamme de ${songKey.label} sur 2 octaves, montee et descente, mains ensemble`,
      contextual: true,
    });
  }

  const allSuggestions = [...contextual, ...STATIC_SUGGESTIONS];

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted">
        Suggestions
      </span>
      <div className="flex flex-wrap gap-1.5">
        {allSuggestions.map((s) => (
          <button
            key={s.label}
            onClick={() => onSelect(s.prompt)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-150 cursor-pointer"
            style={{
              backgroundColor: s.contextual
                ? "rgba(139,92,246,0.06)"
                : "rgba(255,255,255,0.03)",
              border: s.contextual
                ? "1px solid rgba(139,92,246,0.15)"
                : "1px solid rgba(255,255,255,0.06)",
              color: s.contextual
                ? "#a78bfa"
                : "rgba(255,255,255,0.5)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = s.contextual
                ? "rgba(139,92,246,0.3)"
                : "rgba(139,92,246,0.25)";
              e.currentTarget.style.backgroundColor = s.contextual
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.06)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = s.contextual
                ? "rgba(139,92,246,0.15)"
                : "rgba(255,255,255,0.06)";
              e.currentTarget.style.backgroundColor = s.contextual
                ? "rgba(139,92,246,0.06)"
                : "rgba(255,255,255,0.03)";
            }}
          >
            {s.contextual && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5z" />
              </svg>
            )}
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
